import {
  asCardFoil,
  eventQueueDeckRules,
  isLegalDeckList,
  parseDeckList,
  type CardFoilId,
  type DeckList,
  type EventQueueMode,
  type SavedDeckSlot,
} from '@shared/index'
import { useDeckStore, type DeckSlot } from '../stores/deck'
import { useCollectionStore } from '../stores/collection'
import { t } from '../i18n/t'
import lobData from '../assets/lob-cards.json'

interface RawCard {
  id: string | number
  frameType?: string
  typeline?: string[]
  foil?: string
}

const RAW = (lobData as { data: RawCard[] }).data
const BY_ID = new Map(RAW.map((card) => [String(card.id), card]))
const LEGAL_IDS = new Set(BY_ID.keys())
const EXTRA_IDS = new Set(
  RAW.filter(
    (card) => card.frameType === 'fusion' || card.typeline?.includes('Fusion'),
  ).map((card) => String(card.id)),
)

function foilsOf(cards: { foil?: string }[]): CardFoilId[] {
  return cards.map((card) => asCardFoil(card.foil))
}

/**
 * Slim account row → catalog objects the builder already uses.
 */
export function inflateSavedDeck(slot: SavedDeckSlot): DeckSlot {
  const mainDeckCards = slot.main.flatMap((id, i) => {
    const card = BY_ID.get(id)
    if (!card) return []
    return [{ ...card, foil: asCardFoil(slot.mainFoils[i]) }]
  })
  const extraDeckCards = slot.extra.flatMap((id, i) => {
    const card = BY_ID.get(id)
    if (!card) return []
    return [{ ...card, foil: asCardFoil(slot.extraFoils[i]) }]
  })
  return {
    id: slot.id,
    name: slot.name,
    mainDeckCards,
    extraDeckCards,
  }
}

/**
 * Catalog objects → slim list the account API stores.
 */
export function deflateDeckSlot(slot: DeckSlot): SavedDeckSlot {
  return {
    id: slot.id,
    name: slot.name,
    main: slot.mainDeckCards.map((card) => String(card.id)),
    extra: slot.extraDeckCards.map((card) => String(card.id)),
    mainFoils: foilsOf(slot.mainDeckCards),
    extraFoils: foilsOf(slot.extraDeckCards),
  }
}

function cardsOf(slot: DeckSlot | null | undefined): {
  main: any[]
  extra: any[]
} {
  return {
    main: slot?.mainDeckCards ?? [],
    extra: slot?.extraDeckCards ?? [],
  }
}

/** Why the persisted deck cannot start a duel. */
export type SavedDeckBlockReason = 'ok' | 'empty' | 'illegal' | 'unowned'

/**
 * Legality + ownership for one saved slot.
 */
export function getSlotDeckStatus(slot: DeckSlot | null | undefined): {
  reason: SavedDeckBlockReason
  list: DeckList | null
} {
  const { main, extra } = cardsOf(slot)
  const parsed = parseDeckList({
    main: main.map((card) => String(card.id)),
    extra: extra.map((card) => String(card.id)),
    mainFoils: foilsOf(main),
    extraFoils: foilsOf(extra),
  })
  if (!parsed || parsed.main.length === 0) {
    return { reason: 'empty', list: null }
  }
  if (!isLegalDeckList(parsed, LEGAL_IDS, EXTRA_IDS)) {
    return { reason: 'illegal', list: null }
  }
  if (
    !useCollectionStore().deckIsOwned(
      parsed.main,
      parsed.extra,
      parsed.mainFoils,
      parsed.extraFoils,
    )
  ) {
    return { reason: 'unowned', list: null }
  }
  return { reason: 'ok', list: parsed }
}

/**
 * Reads the active Pinia slot and says whether a duel can use it.
 */
export function getSavedDeckStatus(): {
  reason: SavedDeckBlockReason
  list: DeckList | null
} {
  const store = useDeckStore()
  store.ensureIntegrity()
  return getSlotDeckStatus(store.activeSlot)
}

/**
 * Reads the active slot and returns IDs if it is legal and every copy is
 * owned. Otherwise null.
 */
export function getSavedDeckList(): DeckList | null {
  return getSavedDeckStatus().list
}

export type DeckPlayOption = {
  id: string
  name: string
  playable: boolean
}

/** Saved slots for the Home / Lobby picker. */
export function listDeckPlayOptions(): DeckPlayOption[] {
  const store = useDeckStore()
  store.ensureIntegrity()
  return store.decks.map((slot) => ({
    id: slot.id,
    name: slot.name.trim() || t('deck.myDeck'),
    playable: getSlotDeckStatus(slot).reason === 'ok',
  }))
}

/**
 * If the active slot cannot duel, point activeId at the first playable slot.
 */
export function activateFirstPlayable(): void {
  if (getSavedDeckList()) return
  const next = listDeckPlayOptions().find((opt) => opt.playable)
  if (next) useDeckStore().setActiveId(next.id)
}

/** True when at least one saved slot can start a duel. */
export function hasPlayableDeck(): boolean {
  return listDeckPlayOptions().some((opt) => opt.playable)
}

/**
 * Uses a playable slot when the active one is only a draft, then returns
 * that list (or null if none are ready).
 */
export function ensurePlayableDeckList(): DeckList | null {
  activateFirstPlayable()
  return getSavedDeckList()
}

/** True when the active playable slot also matches this event's construction. */
export function eventOwnedDeckFits(list: DeckList, mode: EventQueueMode): boolean {
  const rules = eventQueueDeckRules(mode)
  if (rules.kind !== 'owned') return true
  return isLegalDeckList(list, LEGAL_IDS, EXTRA_IDS, undefined, {
    minMain: rules.minMain,
    maxCopies: rules.maxCopies,
  })
}

/** Lobby label: saved owned deck vs "build a 40-card deck". */
export function savedDeckLabel(): { usingSaved: boolean; name: string } {
  const store = useDeckStore()
  store.ensureIntegrity()
  if (getSavedDeckList()) {
    const name = store.activeSlot?.name || t('deck.myDeck')
    return { usingSaved: true, name }
  }
  return { usingSaved: false, name: t('deck.buildForty') }
}

/**
 * Maps a legal ID list back to catalog objects for the local (vs-AI) engine.
 */
export function resolveLocalCards(
  list: DeckList | null,
): { mainDeck: RawCard[]; extraDeck: RawCard[] } | null {
  if (!list || !isLegalDeckList(list, LEGAL_IDS, EXTRA_IDS)) return null
  const mainDeck: RawCard[] = []
  const extraDeck: RawCard[] = []
  for (let i = 0; i < list.main.length; i++) {
    const id = list.main[i] ?? ''
    const card = BY_ID.get(id)
    if (!card) return null
    mainDeck.push({ ...card, foil: asCardFoil(list.mainFoils?.[i]) })
  }
  for (let i = 0; i < list.extra.length; i++) {
    const id = list.extra[i] ?? ''
    const card = BY_ID.get(id)
    if (!card) return null
    extraDeck.push({ ...card, foil: asCardFoil(list.extraFoils?.[i]) })
  }
  return { mainDeck, extraDeck }
}
